import type {
  CampoFichaEquipe,
  DetalheAbertura,
  DocumentoEquipe,
  Etapa,
  ExecucaoAgente,
  MensagemPronta,
  TaxaJunta,
} from '@contefy/shared';
import {
  ESTADO_NOME,
  ETAPA_INFO,
  ETAPAS_MANUAIS,
  PRIORIDADE_INFO,
  PRIORIDADES,
  type Prioridade,
} from '@contefy/shared';
import { ArrowLeft, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Alerta, Botao, Cartao, Selo, Spinner, Titulo } from '../componentes/ui';
import { centavosParaTexto } from '../lib/formatos';
import { dataHora, equipeApi, rotuloValor } from './api';
import {
  Bandeira,
  EtapaSelo,
  LinhaCampo,
  SinalSituacao,
  StatusDocumento,
  TipoDemandaSelo,
  VisualizadorDocumento,
} from './componentes';
import { useUsuario } from './Layout';
import { MesaNavegador } from './MesaNavegador';

type Aba = 'dados' | 'documentos' | 'mensagens' | 'historico' | 'agentes';

/** Ficha do processo: cabeçalho com ações e abas Dados, Documentos e Histórico. */
export function Abertura() {
  const { id = '' } = useParams();
  const [d, setD] = useState<DetalheAbertura | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aba, setAba] = useState<Aba>('dados');
  const carregar = useCallback(
    () =>
      equipeApi
        .detalhe(id)
        .then(setD)
        .catch((e) => setErro((e as Error).message)),
    [id],
  );
  useEffect(() => {
    carregar();
  }, [carregar]);

  if (erro) return <Alerta tipo="erro">{erro}</Alerta>;
  if (!d)
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-5 w-5" />
      </div>
    );
  const a = d.abertura;
  const marcados = d.ficha.filter((c) => c.marcado).length;

  return (
    <div className="space-y-4">
      <Link
        to="/equipe"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Fila
      </Link>
      <Cabecalho d={d} aoMudar={carregar} />

      {a.etapa !== 'concluida' && a.contratoStatus === 'cancelado' && (
        <Alerta tipo="erro">
          <b>Contrato cancelado no site.</b> O painel de contratos registrou a rescisão deste
          cliente depois de a abertura ter entrado aqui. Confira com o comercial antes de continuar;
          se for para encerrar, mude a etapa para "Concluída" com uma nota.
        </Alerta>
      )}
      {a.etapa !== 'concluida' &&
        a.contratoStatus !== 'cancelado' &&
        /^(cancel|rescind|contrato_cancelado)/i.test(a.cobrancaStatus ?? '') && (
          <Alerta tipo="erro">
            <b>Cobrança encerrada no site.</b> A cobrança recorrente deste cliente foi cancelada.
            Confira com o financeiro antes de continuar.
          </Alerta>
        )}

      {a.etapa === 'aprovacao' && (
        <Alerta tipo={marcados ? 'aviso' : 'sucesso'}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {marcados ? (
                <>
                  <b>{marcados} campo(s)</b> marcados pela IA para conferir antes de aprovar.
                </>
              ) : (
                'Ficha sem marcações. Pronta para aprovar.'
              )}
            </span>
            <Link
              to={`/equipe/aberturas/${a.id}/aprovar`}
              className="inline-flex min-h-9 items-center rounded-lg bg-marca-600 px-3 text-sm font-medium text-white hover:bg-marca-700"
            >
              Abrir aprovação
            </Link>
          </div>
        </Alerta>
      )}

      <div className="flex gap-1 border-b border-slate-200 text-sm">
        {(['dados', 'documentos', 'mensagens', 'historico', 'agentes'] as Aba[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setAba(k)}
            className={`-mb-px border-b-2 px-3 py-2 ${aba === k ? 'border-marca-600 font-medium text-marca-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
          >
            {
              {
                dados: `Dados (${d.ficha.length})`,
                documentos: `Documentos (${d.documentos.length})`,
                mensagens: 'Mensagens',
                historico: `Histórico (${d.historico.length})`,
                agentes: 'Agentes',
              }[k]
            }
          </button>
        ))}
      </div>

      {aba === 'dados' && <AbaDados d={d} aoMudar={carregar} />}
      {aba === 'documentos' && <AbaDocumentos d={d} aoMudar={carregar} />}
      {aba === 'mensagens' && <AbaMensagens d={d} aoMudar={carregar} />}
      {aba === 'historico' && <AbaHistorico d={d} aoMudar={carregar} />}
      {aba === 'agentes' && <AbaAgentes d={d} />}
    </div>
  );
}

function Cabecalho({ d, aoMudar }: { d: DetalheAbertura; aoMudar: () => void }) {
  const a = d.abertura;
  const usuario = useUsuario();
  const [msg, setMsg] = useState<string | null>(null);
  const [etapa, setEtapa] = useState<Etapa>(a.etapa);
  const [motivo, setMotivo] = useState('');
  const [mudando, setMudando] = useState(false);
  const podeAvancar = ETAPAS_MANUAIS.includes(a.etapa) || a.etapa === 'aprovacao';

  return (
    <Cartao>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">
              {a.nomeEmpresa ?? a.nomeContato}
            </h1>
            <TipoDemandaSelo tipo={a.tipoDemanda} />
            <EtapaSelo etapa={a.etapa} />
            <SinalSituacao situacao={a.situacao} />
            <Bandeira prioridade={a.prioridade} comTexto />
            {a.origem === 'fluentforms' && (
              <Selo cor="verde">
                contrato {a.contratoStatus} · 1ª parcela {a.cobrancaStatus?.split('/')[1]}
              </Selo>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {ESTADO_NOME[a.estado]} ·{' '}
            {a.tipoEmpresa === 'EI' ? 'Empresário Individual' : 'Sociedade Limitada'} ·{' '}
            {a.municipio ?? 'município não informado'} · {d.socios.length} sócio(s) ·{' '}
            {a.plano ?? 'plano não informado'}
          </p>
          <p className="text-sm text-slate-600">
            Contato: {a.nomeContato} · {a.telefoneContato}{' '}
            {a.emailContato ? `· ${a.emailContato}` : ''} · na etapa desde {dataHora(a.etapaDesde)}
            {a.wpEntryId && <span className="text-slate-400"> · solicitação #{a.wpEntryId}</span>}
          </p>
          <p className="mt-1 text-xs">
            <a
              href={a.linkPortal}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1 text-marca-700 hover:underline"
            >
              Página do cliente <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-slate-600">Prioridade</span>
            <select
              className="!w-auto !py-1"
              value={a.prioridade}
              onChange={async (e) => {
                await equipeApi.prioridade(a.id, e.target.value as Prioridade);
                aoMudar();
              }}
            >
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {PRIORIDADE_INFO[p].titulo}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-slate-600">Responsável</span>
            <input
              className="!w-40 !py-1"
              defaultValue={a.responsavelId ?? ''}
              placeholder={usuario?.sub}
              onBlur={async (e) => {
                const v = e.target.value.trim() || null;
                if (v !== (a.responsavelId ?? null)) {
                  await equipeApi.responsavel(a.id, v);
                  aoMudar();
                }
              }}
            />
          </label>
          {podeAvancar && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <select
                value={etapa}
                onChange={(e) => setEtapa(e.target.value as Etapa)}
                className="!w-auto !py-1"
              >
                {ETAPAS_MANUAIS.map((e) => (
                  <option key={e} value={e}>
                    {ETAPA_INFO[e].numero}. {ETAPA_INFO[e].titulo}
                  </option>
                ))}
              </select>
              <input
                className="!w-56 !py-1"
                placeholder="motivo / o que foi feito"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
              <Botao
                variante="secundario"
                className="!min-h-8 !py-1 text-sm"
                carregando={mudando}
                disabled={etapa === a.etapa || motivo.trim().length < 2}
                onClick={async () => {
                  setMudando(true);
                  setMsg(null);
                  try {
                    await equipeApi.etapa(a.id, etapa, motivo.trim());
                    setMotivo('');
                    aoMudar();
                  } catch (e) {
                    setMsg((e as Error).message);
                  } finally {
                    setMudando(false);
                  }
                }}
              >
                Mover etapa
              </Botao>
            </div>
          )}
          {msg && <span className="text-xs text-red-700">{msg}</span>}
        </div>
      </div>
      {a.descricaoDemanda && (
        <div className="mt-3">
          <Alerta tipo="info">
            <b>Demanda:</b> <span className="whitespace-pre-wrap">{a.descricaoDemanda}</span>
          </Alerta>
        </div>
      )}
      {a.observacoes && (
        <div className="mt-3">
          <Alerta tipo="aviso">
            <pre className="whitespace-pre-wrap font-sans">{a.observacoes}</pre>
          </Alerta>
        </div>
      )}
    </Cartao>
  );
}

const GRUPOS: { chave: CampoFichaEquipe['grupo']; titulo: string }[] = [
  { chave: 'empresa', titulo: 'Empresa' },
  { chave: 'atividade', titulo: 'Atividade' },
  { chave: 'tributario', titulo: 'Porte e regime' },
  { chave: 'endereco', titulo: 'Endereço da empresa' },
  { chave: 'capital', titulo: 'Capital' },
];

export function AbaDados({
  d,
  aoMudar,
  somenteMarcados = false,
}: {
  d: DetalheAbertura;
  aoMudar: () => void;
  somenteMarcados?: boolean;
}) {
  const a = d.abertura;
  const campos = somenteMarcados ? d.ficha.filter((c) => c.marcado) : d.ficha;
  const porSocio = useMemo(() => {
    const m = new Map<string, CampoFichaEquipe[]>();
    for (const c of campos.filter((c) => c.grupo === 'socio'))
      m.set(c.socio_id ?? '', [...(m.get(c.socio_id ?? '') ?? []), c]);
    return m;
  }, [campos]);
  const [remontando, setRemontando] = useState(false);

  if (d.ficha.length === 0) {
    return (
      <Cartao>
        <Titulo
          sub={
            a.etapa === 'ficha_ia'
              ? 'A IA está montando a ficha. Esta página atualiza em instantes.'
              : 'A ficha ainda não foi montada.'
          }
        >
          Ficha de abertura
        </Titulo>
        {a.questionario && <ResumoQuestionario q={a.questionario} />}
      </Cartao>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-4">
        {GRUPOS.map((g) => {
          const lista = campos.filter((c) => c.grupo === g.chave);
          if (!lista.length) return null;
          return (
            <Cartao key={g.chave}>
              <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">
                {g.titulo}
              </h3>
              {lista.map((c) => (
                <LinhaCampo key={c.chave} campo={c} aberturaId={a.id} aoMudar={aoMudar} />
              ))}
            </Cartao>
          );
        })}
        {[...porSocio.entries()].map(([socioId, lista]) => {
          const s = d.socios.find((x) => x.id === socioId);
          return (
            <Cartao key={socioId}>
              <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Sócio: {s?.nome ?? socioId} {s?.administrador ? '· administrador' : ''}
              </h3>
              {lista.map((c) => (
                <LinhaCampo key={c.chave} campo={c} aberturaId={a.id} aoMudar={aoMudar} />
              ))}
            </Cartao>
          );
        })}
      </div>
      <div className="space-y-4">
        {a.propostaIa && <ParecerIa p={a.propostaIa} />}
        <Cartao>
          <Titulo sub={a.fichaMontadaEm ? `Montada em ${dataHora(a.fichaMontadaEm)}` : undefined}>
            Ficha
          </Titulo>
          {a.fichaAprovadaEm ? (
            <p className="flex items-center gap-2 text-sm text-emerald-800">
              <CheckCircle2 className="h-4 w-4" /> Aprovada por {a.fichaAprovadaPor} em{' '}
              {dataHora(a.fichaAprovadaEm)}
            </p>
          ) : (
            <Botao
              variante="secundario"
              className="!min-h-9 !py-1.5 text-sm"
              carregando={remontando}
              onClick={async () => {
                setRemontando(true);
                try {
                  await equipeApi.remontar(a.id);
                  aoMudar();
                } finally {
                  setRemontando(false);
                }
              }}
            >
              <RefreshCw className="h-4 w-4" /> Remontar com a IA
            </Botao>
          )}
          <p className="mt-2 text-xs text-slate-500">
            Remontar refaz a leitura cruzada. Correções feitas pela equipe são preservadas.
          </p>
        </Cartao>
      </div>
    </div>
  );
}

function ParecerIa({ p }: { p: NonNullable<DetalheAbertura['abertura']['propostaIa']> }) {
  return (
    <Cartao>
      <Titulo sub="Nome, CNAEs, porte e regime, com o raciocínio em poucas linhas.">
        Parecer da IA
      </Titulo>
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Nome empresarial</dt>
          <dd className="font-medium">{p.nome_empresarial.principal}</dd>
          {p.nome_empresarial.alternativas.length > 0 && (
            <dd className="text-slate-600">
              Alternativas: {p.nome_empresarial.alternativas.join(' · ')}
            </dd>
          )}
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">CNAEs</dt>
          {p.cnaes.map((c) => (
            <dd key={c.codigo} className={c.principal ? 'font-medium' : ''}>
              {c.codigo} {c.descricao} {c.principal && <Selo cor="azul">principal</Selo>}
            </dd>
          ))}
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Porte e regime</dt>
          <dd>
            {p.porte} · {p.regime_tributario.replace('_', ' ')}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Raciocínio</dt>
          <dd className="text-slate-700">{p.raciocinio}</dd>
        </div>
        {p.alertas.length > 0 && (
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Alertas</dt>
            {p.alertas.map((al) => (
              <dd key={al} className="text-amber-800">
                • {al}
              </dd>
            ))}
          </div>
        )}
      </dl>
    </Cartao>
  );
}

function ResumoQuestionario({
  q,
}: {
  q: NonNullable<DetalheAbertura['abertura']['questionario']>;
}) {
  return (
    <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
      <div>
        <dt className="text-xs uppercase text-slate-500">Nomes</dt>
        <dd>{q.nome_empresarial_opcoes.join(' · ')}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase text-slate-500">Capital</dt>
        <dd>{centavosParaTexto(q.capital_social_centavos)}</dd>
      </div>
      <div className="sm:col-span-2">
        <dt className="text-xs uppercase text-slate-500">Atividades</dt>
        <dd className="whitespace-pre-wrap">{q.atividades_descricao}</dd>
      </div>
    </dl>
  );
}

function AbaDocumentos({ d, aoMudar }: { d: DetalheAbertura; aoMudar: () => void }) {
  const [aberto, setAberto] = useState<DocumentoEquipe | null>(d.documentos[0] ?? null);
  const a = d.abertura;
  return (
    <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
      <div className="space-y-3">
        {d.checklist.map((item) => {
          const docs = d.documentos.filter((x) => item.documento_ids.includes(x.id));
          return (
            <Cartao key={item.chave} className="!p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-slate-900">{item.titulo}</p>
                  {item.socio_nome && <p className="text-xs text-slate-500">{item.socio_nome}</p>}
                </div>
                <StatusDocumento status={item.status} />
              </div>
              {docs.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => setAberto(doc)}
                  className={`mt-2 flex w-full items-center justify-between rounded-lg border px-2.5 py-1.5 text-left text-xs ${aberto?.id === doc.id ? 'border-marca-500 bg-marca-50' : 'border-slate-200 hover:bg-slate-50'}`}
                >
                  <span className="truncate">{doc.nomeOriginal}</span>
                  <span className="ml-2 shrink-0 text-slate-500">
                    {doc.confianca ? `${Math.round(Number(doc.confianca) * 100)}%` : ''}
                  </span>
                </button>
              ))}
              {docs.length === 0 && (
                <p className="mt-2 text-xs text-slate-500">
                  Não veio no formulário. Se for necessário, a equipe pede ao cliente.
                </p>
              )}
            </Cartao>
          );
        })}
      </div>
      <div>
        {aberto ? (
          <Cartao>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-medium text-slate-900">{aberto.nomeOriginal}</p>
                <p className="text-xs text-slate-500">
                  {aberto.mime} · {(aberto.tamanhoBytes / 1024).toFixed(0)} KB ·{' '}
                  <StatusDocumento status={aberto.status} />
                  {aberto.motivo && <span className="text-amber-800"> · {aberto.motivo}</span>}
                </p>
              </div>
              {aberto.status !== 'aceito' && (
                <div className="flex gap-2">
                  <Botao
                    className="!min-h-8 !py-1 text-sm"
                    onClick={() =>
                      equipeApi.conferirDocumento(a.id, aberto.id, 'aceito').then(aoMudar)
                    }
                  >
                    Aceitar
                  </Botao>
                  <Botao
                    variante="perigo"
                    className="!min-h-8 !py-1 text-sm"
                    onClick={() => {
                      const m =
                        prompt('Motivo da rejeição (documento errado, vencido, ilegível):') ?? '';
                      if (m.trim())
                        equipeApi
                          .conferirDocumento(a.id, aberto.id, 'rejeitado', m.trim())
                          .then(aoMudar);
                    }}
                  >
                    Rejeitar
                  </Botao>
                </div>
              )}
            </div>
            <VisualizadorDocumento aberturaId={a.id} documento={aberto} altura={560} />
            {aberto.dadosExtraidos && (
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-slate-700">Dados lidos pela IA</summary>
                <DadosExtraidos dados={aberto.dadosExtraidos} />
              </details>
            )}
            {aberto.erroExtracao && (
              <p className="mt-2 text-xs text-red-700">Leitura: {aberto.erroExtracao}</p>
            )}
          </Cartao>
        ) : (
          <Cartao>
            <p className="text-sm text-slate-600">Nenhum documento.</p>
          </Cartao>
        )}
      </div>
    </div>
  );
}

function DadosExtraidos({ dados }: { dados: Record<string, unknown> }) {
  const linhas = Object.entries(dados).filter(([k]) => k !== 'tipo' && k !== 'observacoes');
  return (
    <dl className="mt-2 grid gap-1 sm:grid-cols-2">
      {linhas.map(([k, v]) => {
        const c = v as { valor?: unknown; confianca?: number; trecho?: string | null } | unknown;
        const obj =
          c && typeof c === 'object' && 'valor' in (c as object)
            ? (c as { valor?: unknown; confianca?: number; trecho?: string | null })
            : null;
        return (
          <div key={k} className="rounded-lg bg-slate-50 px-2.5 py-1.5">
            <dt className="text-[11px] uppercase tracking-wide text-slate-500">
              {k.replace(/_/g, ' ')}
            </dt>
            <dd className="text-sm text-slate-900">
              {obj ? rotuloValor(obj.valor) : rotuloValor(v)}
              {obj?.confianca != null && (
                <span className="ml-1 text-xs text-slate-500">
                  ({Math.round(obj.confianca * 100)}%)
                </span>
              )}
            </dd>
            {obj?.trecho && <dd className="text-xs italic text-slate-500">“{obj.trecho}”</dd>}
          </div>
        );
      })}
    </dl>
  );
}

const ATOR: Record<string, string> = {
  sistema: 'Sistema',
  cliente: 'Cliente',
  ia: 'IA',
  robo: 'Robô',
  equipe: 'Equipe',
};

function AbaHistorico({ d, aoMudar }: { d: DetalheAbertura; aoMudar: () => void }) {
  const [nota, setNota] = useState('');
  const [salvando, setSalvando] = useState(false);
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Cartao>
        <Titulo sub="Quem fez o quê e quando. É o registro de auditoria.">Histórico</Titulo>
        <ol className="space-y-3">
          {[...d.historico].reverse().map((e) => (
            <li key={e.id} className="flex gap-3 text-sm">
              <span className="w-28 shrink-0 text-xs text-slate-500">{dataHora(e.criadoEm)}</span>
              <div>
                <span className="mr-2 rounded bg-slate-100 px-1.5 text-[11px] font-medium text-slate-600">
                  {ATOR[e.ator] ?? e.ator}
                  {e.atorId ? ` · ${e.atorId}` : ''}
                </span>
                <span className="text-slate-800">{e.descricao}</span>
                {e.tipo === 'ficha.lacunas' && Array.isArray(e.dados?.lacunas) && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-slate-600">
                    {(e.dados.lacunas as { campo: string; problema: string }[]).map((l) => (
                      <li key={l.campo}>{l.problema}</li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ol>
      </Cartao>
      <Cartao>
        <Titulo>Anotação</Titulo>
        <textarea
          rows={4}
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Ex.: liguei para o cliente, vai enviar o IPTU amanhã"
        />
        <Botao
          className="mt-2 !min-h-9 !py-1.5 text-sm"
          carregando={salvando}
          disabled={nota.trim().length === 0}
          onClick={async () => {
            setSalvando(true);
            try {
              await equipeApi.nota(d.abertura.id, nota.trim());
              setNota('');
              aoMudar();
            } finally {
              setSalvando(false);
            }
          }}
        >
          Registrar
        </Botao>
      </Cartao>
    </div>
  );
}

/** Tela focada de aprovação: só os campos marcados, documento ao lado, aprovar em um clique. */
export function Aprovacao() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState<DetalheAbertura | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [contrato, setContrato] = useState<'padrao' | 'proprio'>('padrao');
  const [obs, setObs] = useState('');
  const [aprovando, setAprovando] = useState(false);
  const [docAberto, setDocAberto] = useState<string | null>(null);
  const carregar = useCallback(
    () =>
      equipeApi
        .detalhe(id)
        .then(setD)
        .catch((e) => setErro((e as Error).message)),
    [id],
  );
  useEffect(() => {
    carregar();
  }, [carregar]);
  if (erro) return <Alerta tipo="erro">{erro}</Alerta>;
  if (!d)
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-5 w-5" />
      </div>
    );
  const a = d.abertura;
  const marcados = d.ficha.filter((c) => c.marcado);
  const docsDosMarcados = [
    ...new Set(marcados.map((c) => c.documento_id).filter(Boolean)),
  ] as string[];
  const docId = docAberto ?? docsDosMarcados[0] ?? d.documentos[0]?.id ?? null;
  const doc = d.documentos.find((x) => x.id === docId) ?? null;

  return (
    <div className="space-y-4">
      <Link
        to={`/equipe/aberturas/${a.id}`}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Ficha do processo
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold text-slate-900">
          Aprovar ficha · {a.nomeEmpresa ?? a.nomeContato}
        </h1>
        <EtapaSelo etapa={a.etapa} />
      </div>
      {a.etapa !== 'aprovacao' && (
        <Alerta tipo="aviso">
          Esta abertura não está em aprovação (etapa atual: {ETAPA_INFO[a.etapa].titulo}).
        </Alerta>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Cartao>
            <Titulo
              sub={
                marcados.length
                  ? 'Corrija ou confirme cada um. O resto da ficha está na aba Dados.'
                  : 'Nenhum campo marcado.'
              }
            >
              {marcados.length} campo(s) para conferir
            </Titulo>
            {marcados.map((c) => (
              <div key={c.chave}>
                <LinhaCampo campo={c} aberturaId={a.id} aoMudar={carregar} />
                {c.documento_id && c.documento_id !== doc?.id && (
                  <button
                    type="button"
                    className="-mt-1 mb-2 text-xs text-marca-700 hover:underline"
                    onClick={() => setDocAberto(c.documento_id)}
                  >
                    Ver o documento de origem
                  </button>
                )}
              </div>
            ))}
          </Cartao>
          {a.propostaIa && (
            <Cartao>
              <Titulo>Parecer da IA</Titulo>
              <p className="text-sm text-slate-700">{a.propostaIa.raciocinio}</p>
              <p className="mt-2 text-sm">
                <b>{a.propostaIa.nome_empresarial.principal}</b> · {a.propostaIa.porte} ·{' '}
                {a.propostaIa.regime_tributario.replace('_', ' ')}
              </p>
              <ul className="mt-1 text-sm text-slate-700">
                {a.propostaIa.cnaes.map((c) => (
                  <li key={c.codigo}>
                    {c.codigo} {c.descricao} {c.principal ? '(principal)' : ''}
                  </li>
                ))}
              </ul>
              {a.propostaIa.alertas.map((al) => (
                <p key={al} className="mt-1 text-sm text-amber-800">
                  • {al}
                </p>
              ))}
            </Cartao>
          )}
          <Cartao>
            <Titulo sub="Contrato padrão da Junta dá registro automático; contrato próprio vai a analista.">
              Aprovar
            </Titulo>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="contrato"
                  checked={contrato === 'padrao'}
                  onChange={() => setContrato('padrao')}
                />{' '}
                Contrato padrão
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="contrato"
                  checked={contrato === 'proprio'}
                  onChange={() => setContrato('proprio')}
                />{' '}
                Contrato próprio
              </label>
            </div>
            <textarea
              className="mt-3"
              rows={2}
              placeholder="Observação (opcional)"
              value={obs}
              onChange={(e) => setObs(e.target.value)}
            />
            <Botao
              className="mt-3 w-full"
              disabled={a.etapa !== 'aprovacao'}
              carregando={aprovando}
              onClick={async () => {
                if (
                  marcados.length &&
                  !confirm(`Ainda há ${marcados.length} campo(s) marcados. Aprovar assim mesmo?`)
                )
                  return;
                setAprovando(true);
                try {
                  await equipeApi.aprovar(a.id, contrato, obs.trim() || undefined);
                  nav(`/equipe/aberturas/${a.id}`);
                } catch (e) {
                  setErro((e as Error).message);
                } finally {
                  setAprovando(false);
                }
              }}
            >
              <CheckCircle2 className="h-4 w-4" /> Aprovar ficha
            </Botao>
          </Cartao>
        </div>
        <div className="space-y-3 lg:sticky lg:top-20 lg:self-start">
          <div className="flex flex-wrap gap-1.5">
            {d.documentos.map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setDocAberto(x.id)}
                className={`rounded-full border px-2.5 py-1 text-xs ${doc?.id === x.id ? 'border-marca-500 bg-marca-50 text-marca-900' : 'border-slate-200 bg-white text-slate-600'}`}
              >
                {x.tipo} ·{' '}
                {d.socios.find((s) => s.id === x.socioId)?.nome.split(' ')[0] ?? 'empresa'}
              </button>
            ))}
          </div>
          {doc ? (
            <VisualizadorDocumento aberturaId={a.id} documento={doc} altura={640} />
          ) : (
            <Cartao>Nenhum documento para mostrar.</Cartao>
          )}
        </div>
      </div>
    </div>
  );
}

const STATUS_EXEC: Record<ExecucaoAgente['status'], { rotulo: string; cor: string }> = {
  executando: { rotulo: 'Executando', cor: 'bg-sky-100 text-sky-800' },
  concluida: { rotulo: 'Concluída', cor: 'bg-emerald-100 text-emerald-800' },
  aguardando_humano: { rotulo: 'Aguardando a equipe', cor: 'bg-amber-100 text-amber-800' },
  falhou: { rotulo: 'Falhou', cor: 'bg-red-100 text-red-800' },
  cancelada: { rotulo: 'Cancelada', cor: 'bg-slate-100 text-slate-600' },
};

/** Teste de login do Agente da Junta PR: só leitura, para no captcha e pede a equipe. */
const OBJETIVO_TESTE_LOGIN_PR =
  'Teste de login no gov.br com o certificado do cofre, SOMENTE LEITURA, seguindo o manual da JUCEPAR. (1) Abra https://www.empresafacil.pr.gov.br com com_certificado=true e feche todos os avisos/cookies (Ciente) até ver não listar diálogos. (2) Vá em Abertura de Empresa > Abertura de Matriz (o botão Efetuar Login da página inicial NÃO é o caminho). O portal leva ao login do gov.br. Tire captura a cada tela nova. (3) Se já aparecer logado (sessão guardada), pule para o item 5. Na tela do gov.br clique em Seu certificado digital. (4) Se aparecer um desafio de imagens (captcha), tire captura e chame pedir_intervencao com motivo "captcha do gov.br" e o_que_fazer "resolver o captcha e concluir o login por certificado; quando a tela do Empresa Fácil (ou a do gov.br logado) aparecer, clicar em Pronto, pode continuar". Quando a equipe continuar, use navegador.ver e tire captura. (5) Relate: autenticou? qual nome/CNPJ apareceu? Se o gov.br recusou o certificado, copie a mensagem exata. Se autenticou, chame navegador.guardar_login, descreva a primeira tela logada e conclua SEM preencher nada. Não crie conta, não inicie consulta prévia, não altere dado algum.';

/** O que cada agente fez nesta abertura, passo a passo. */
function AbaAgentes({ d }: { d: DetalheAbertura }) {
  const usuario = useUsuario();
  const [lista, setLista] = useState<ExecucaoAgente[] | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const [rodando, setRodando] = useState(false);
  const [mesa, setMesa] = useState<ExecucaoAgente | null>(null);
  const carregar = useCallback(() => {
    equipeApi
      .agentesDaAbertura(d.abertura.id)
      .then(setLista)
      .catch(() => setLista([]));
  }, [d.abertura.id]);
  useEffect(() => {
    carregar();
  }, [carregar]);
  // Enquanto algum agente está rodando, atualiza a lista sozinho para a equipe acompanhar.
  const emAndamento = lista?.some((e) => e.status === 'executando') ?? false;
  useEffect(() => {
    if (!emAndamento) return;
    const t = setInterval(carregar, 4000);
    return () => clearInterval(t);
  }, [emAndamento, carregar]);

  const rodarAgente = async (agente: string, objetivo: string) => {
    setRodando(true);
    try {
      await equipeApi.executarAgente(d.abertura.id, agente, objetivo);
      carregar();
    } finally {
      setRodando(false);
    }
  };
  const rodarConsultor = async () => {
    setRodando(true);
    try {
      await equipeApi.executarAgente(
        d.abertura.id,
        'consultor_enquadramento',
        'Revisar a ficha desta abertura: confira nome empresarial, CNAEs, natureza jurídica e regime; marque para revisão o que estiver inconsistente e conclua com um resumo para a equipe.',
      );
      carregar();
    } finally {
      setRodando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <Titulo sub="Cada rodada de um agente de IA nesta abertura, com os passos e o resultado. Nada aqui é enviado ao cliente.">
          Agentes
        </Titulo>
        {usuario?.perfil === 'administrador' && (
          <div className="flex flex-wrap gap-2">
            <Botao variante="secundario" carregando={rodando} onClick={rodarConsultor}>
              Pedir revisão ao Consultor de Enquadramento
            </Botao>
            {d.abertura.estado === 'PR' && (
              <Botao
                variante="secundario"
                carregando={rodando}
                onClick={() => rodarAgente('junta_pr', OBJETIVO_TESTE_LOGIN_PR)}
                title="Só leitura: o agente vai até o login do gov.br com o certificado do cofre e para no captcha para a equipe resolver."
              >
                Testar login no gov.br (Junta PR)
              </Botao>
            )}
          </div>
        )}
      </div>
      {lista === null && <p className="text-sm text-slate-500">Carregando…</p>}
      {lista?.length === 0 && (
        <p className="text-sm text-slate-500">Nenhum agente atuou nesta abertura ainda.</p>
      )}
      <div className="space-y-2">
        {lista?.map((e) => (
          <Cartao key={e.id} className="space-y-2">
            <button
              type="button"
              className="flex w-full flex-wrap items-center justify-between gap-2 text-left"
              onClick={() => setAberta(aberta === e.id ? null : e.id)}
            >
              <div>
                <div className="font-medium text-slate-900">{e.agente_nome}</div>
                <div className="text-sm text-slate-600">{e.objetivo}</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>{new Date(e.iniciado_em).toLocaleString('pt-BR')}</span>
                <span
                  className={`rounded-full px-2 py-0.5 font-medium ${STATUS_EXEC[e.status].cor}`}
                >
                  {STATUS_EXEC[e.status].rotulo}
                </span>
              </div>
            </button>
            {e.erro && <p className="text-sm text-red-700">{e.erro}</p>}
            {e.status === 'aguardando_humano' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p>
                  <b>Por que parou:</b>{' '}
                  {textoResultado(e.resultado, 'motivo') ?? 'o agente pediu a equipe'}
                </p>
                {textoResultado(e.resultado, 'o_que_fazer') && (
                  <p>
                    <b>O que fazer:</b> {textoResultado(e.resultado, 'o_que_fazer')}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Botao onClick={() => setMesa(e)}>Assumir navegador</Botao>
                </div>
              </div>
            )}
            {aberta === e.id && (
              <ol className="space-y-1.5 border-l-2 border-slate-200 pl-3 text-sm">
                {e.passos.map((p) => (
                  <li key={p.id}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-slate-400">
                        {p.tipo}
                      </span>
                      <span className="font-medium text-slate-800">{p.titulo}</span>
                      {p.duracao_ms != null && (
                        <span className="text-xs text-slate-400">
                          {(p.duracao_ms / 1000).toFixed(1)}s
                        </span>
                      )}
                    </div>
                    {p.detalhe && <p className="whitespace-pre-wrap text-slate-700">{p.detalhe}</p>}
                    {p.captura_url && (
                      <a href={p.captura_url} target="_blank" rel="noreferrer">
                        <img
                          src={p.captura_url}
                          alt={p.titulo}
                          className="mt-1 max-h-64 rounded border border-slate-200"
                        />
                      </a>
                    )}
                    {p.resultado != null && p.tipo !== 'pensamento' && (
                      <pre className="mt-1 max-h-48 overflow-auto rounded bg-slate-50 p-2 text-xs text-slate-600">
                        {JSON.stringify(p.resultado, null, 2).slice(0, 3000)}
                      </pre>
                    )}
                  </li>
                ))}
                {e.resultado != null && e.status === 'concluida' && (
                  <li className="pt-1 text-slate-800">
                    <b>Resultado:</b>{' '}
                    {typeof (e.resultado as { resumo?: unknown }).resumo === 'string'
                      ? (e.resultado as { resumo: string }).resumo
                      : JSON.stringify(e.resultado).slice(0, 1000)}
                  </li>
                )}
              </ol>
            )}
          </Cartao>
        ))}
      </div>
      {mesa && (
        <MesaNavegador
          execucaoId={mesa.id}
          motivo={textoResultado(mesa.resultado, 'motivo')}
          oQueFazer={textoResultado(mesa.resultado, 'o_que_fazer')}
          aoFechar={() => setMesa(null)}
          aoRetomar={() => {
            setMesa(null);
            carregar();
          }}
        />
      )}
    </div>
  );
}

function textoResultado(resultado: unknown, chave: string): string | null {
  const v = (resultado as Record<string, unknown> | null)?.[chave];
  return typeof v === 'string' && v ? v : null;
}

/** Textos prontos para a equipe mandar ao cliente ou ao sócio com um clique. Nada sai sozinho. */
function AbaMensagens({ d, aoMudar }: { d: DetalheAbertura; aoMudar: () => void }) {
  const [lista, setLista] = useState<MensagemPronta[] | null>(null);
  useEffect(() => {
    equipeApi
      .mensagensProntas(d.abertura.id)
      .then(setLista)
      .catch(() => setLista([]));
  }, [d.abertura.id]);
  return (
    <div className="space-y-4">
      <Titulo sub="Confira o texto, preencha o que estiver entre colchetes e clique. O WhatsApp abre com o número e a mensagem prontos; o e-mail abre no seu programa de e-mail. Fica registrado no histórico.">
        Mensagens prontas
      </Titulo>
      <div className="grid gap-3 lg:grid-cols-2">
        <TaxaCartao
          d={d}
          aoSalvar={() => {
            aoMudar();
            equipeApi
              .mensagensProntas(d.abertura.id)
              .then(setLista)
              .catch(() => undefined);
          }}
        />
        <AssinaturaCartao
          d={d}
          aoSalvar={() => {
            aoMudar();
            equipeApi
              .mensagensProntas(d.abertura.id)
              .then(setLista)
              .catch(() => undefined);
          }}
        />
      </div>
      {lista === null && <p className="text-sm text-slate-500">Carregando…</p>}
      <div className="grid gap-3 lg:grid-cols-2">
        {lista?.map((m, i) => (
          <MensagemProntaCartao
            key={`${m.modelo}-${i}`}
            m={m}
            aberturaId={d.abertura.id}
            aoRegistrar={aoMudar}
          />
        ))}
      </div>
    </div>
  );
}

function MensagemProntaCartao({
  m,
  aberturaId,
  aoRegistrar,
}: {
  m: MensagemPronta;
  aberturaId: string;
  aoRegistrar: () => void;
}) {
  const [texto, setTexto] = useState(m.texto);
  const lacunas = [...texto.matchAll(/\[([^\]]+)\]/g)].map((x) => x[1]);
  const registrar = (canal: 'whatsapp' | 'email') =>
    equipeApi
      .mensagemAberta(aberturaId, { modelo: m.modelo, canal, destinatario: m.destinatario.nome })
      .then(aoRegistrar)
      .catch(() => undefined);
  const abrirWhatsApp = () => {
    if (!m.destinatario.telefone) return;
    const numero = m.whatsapp_url ? new URL(m.whatsapp_url).pathname.replace('/', '') : '';
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
    registrar('whatsapp');
  };
  const abrirEmail = () => {
    if (!m.destinatario.email) return;
    window.location.href = `mailto:${m.destinatario.email}?subject=${encodeURIComponent(m.assunto)}&body=${encodeURIComponent(texto)}`;
    registrar('email');
  };
  return (
    <Cartao className="space-y-2">
      <div>
        <div className="font-semibold text-slate-900">{m.titulo}</div>
        <div className="text-xs text-slate-500">{m.dica}</div>
      </div>
      <div className="text-sm text-slate-700">
        Para: <b>{m.destinatario.nome}</b>
        {m.destinatario.tipo === 'socio' && <span className="text-slate-500"> (sócio)</span>}
        <span className="text-slate-500">
          {' '}
          · {m.destinatario.telefone ?? 'sem telefone'} · {m.destinatario.email ?? 'sem e-mail'}
        </span>
      </div>
      <textarea
        className="min-h-40 w-full rounded-lg border border-slate-300 p-2 text-sm"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
      />
      {lacunas.length > 0 && (
        <p className="text-xs text-amber-700">Preencha antes de enviar: {lacunas.join(', ')}.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Botao
          onClick={abrirWhatsApp}
          disabled={!m.destinatario.telefone || lacunas.length > 0}
          title={!m.destinatario.telefone ? 'Destinatário sem telefone' : undefined}
        >
          Abrir no WhatsApp
        </Botao>
        <Botao
          variante="secundario"
          onClick={abrirEmail}
          disabled={!m.destinatario.email || lacunas.length > 0}
          title={!m.destinatario.email ? 'Destinatário sem e-mail' : undefined}
        >
          Enviar por e-mail
        </Botao>
        <Botao variante="secundario" onClick={() => navigator.clipboard?.writeText(texto)}>
          Copiar texto
        </Botao>
      </div>
    </Cartao>
  );
}

/** Taxa da Junta: o agente preenche ao gerar a guia; até lá a equipe preenche à mão. Alimenta a mensagem pronta. */
/**
 * Taxa da Junta. O caminho normal da equipe é só anexar a guia: o link entra sozinho na mensagem
 * "Pagar a taxa". Valor, vencimento e PIX são opcionais (o agente preenche quando gera a guia).
 */
function TaxaCartao({ d, aoSalvar }: { d: DetalheAbertura; aoSalvar: () => void }) {
  const t: TaxaJunta | null = d.abertura.taxa;
  const [detalhes, setDetalhes] = useState(Boolean(t?.valor || t?.pix));
  const [valor, setValor] = useState(t?.valor ?? '');
  const [vencimento, setVencimento] = useState(t?.vencimento ?? '');
  const [pix, setPix] = useState(t?.pix ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const anexar = async () => {
    const arquivo = arquivoRef.current?.files?.[0];
    if (!arquivo) return;
    setSalvando(true);
    setErro(null);
    try {
      await equipeApi.anexarGuia(d.abertura.id, arquivo);
      if (arquivoRef.current) arquivoRef.current.value = '';
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  const salvar = async (extra: Partial<{ paga_em: string | null }> = {}) => {
    setSalvando(true);
    setErro(null);
    try {
      await equipeApi.salvarTaxa(d.abertura.id, {
        valor: valor || null,
        vencimento: vencimento || null,
        pix: pix || null,
        ...extra,
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Cartao className="space-y-3">
      <div>
        <div className="font-semibold text-slate-900">Taxa da Junta</div>
        <div className="text-xs text-slate-500">
          Anexe a guia (PDF ou imagem). A mensagem "Pagar a taxa da Junta" sai com o link para o
          cliente.
          {t?.paga_em && (
            <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
              paga em {new Date(t.paga_em).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>
      </div>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      {t?.guia_chave && (
        <p className="text-sm text-slate-700">
          Guia anexada: <b>{t.guia_nome}</b>
          {t.guia_anexada_em && (
            <span className="text-slate-500">
              {' '}
              em {new Date(t.guia_anexada_em).toLocaleString('pt-BR')}
            </span>
          )}
        </p>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="text-slate-600">
            {t?.guia_chave ? 'Trocar a guia' : 'Arquivo da guia'}
          </span>
          <input
            ref={arquivoRef}
            type="file"
            accept=".pdf,image/*"
            className="mt-1 block text-sm"
          />
        </label>
        <Botao carregando={salvando} onClick={anexar}>
          Anexar guia
        </Botao>
        {!t?.paga_em && (t?.guia_chave || t?.valor) && (
          <Botao
            variante="secundario"
            carregando={salvando}
            onClick={() => salvar({ paga_em: new Date().toISOString() })}
          >
            Marcar como paga
          </Botao>
        )}
      </div>
      <button
        type="button"
        className="text-xs text-slate-500 underline"
        onClick={() => setDetalhes(!detalhes)}
      >
        {detalhes
          ? 'Esconder valor, vencimento e PIX'
          : 'Informar valor, vencimento e PIX (opcional)'}
      </button>
      {detalhes && (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-sm">
              <span className="text-slate-600">Valor (R$)</span>
              <input
                className="mt-1 w-full"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="247,00"
              />
            </label>
            <label className="text-sm">
              <span className="text-slate-600">Vencimento</span>
              <input
                className="mt-1 w-full"
                type="date"
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="text-slate-600">Código PIX copia e cola</span>
            <textarea
              className="mt-1 min-h-16 w-full rounded-lg border border-slate-300 p-2 text-xs"
              value={pix}
              onChange={(e) => setPix(e.target.value)}
            />
          </label>
          <Botao variante="secundario" carregando={salvando} onClick={() => salvar()}>
            Salvar detalhes
          </Botao>
        </div>
      )}
    </Cartao>
  );
}

/** Link (PR) ou número do processo (MG/SC): entra na mensagem de assinatura de cada sócio. */
function AssinaturaCartao({ d, aoSalvar }: { d: DetalheAbertura; aoSalvar: () => void }) {
  const a = d.abertura;
  const porLink = a.estado === 'PR';
  const [valor, setValor] = useState((porLink ? a.assinatura?.link : a.assinatura?.processo) ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await equipeApi.assinatura(
        a.id,
        porLink ? { link: valor || null } : { processo: valor || null },
      );
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Cartao className="space-y-3">
      <div>
        <div className="font-semibold text-slate-900">
          Assinatura digital ({ESTADO_NOME[a.estado]})
        </div>
        <div className="text-xs text-slate-500">
          {porLink
            ? 'A Junta do Paraná manda um link de assinatura. Cole aqui e a mensagem de cada sócio sai pronta.'
            : 'Esta Junta assina pelo número do processo. Informe aqui e a mensagem de cada sócio sai pronta.'}
        </div>
      </div>
      {erro && <Alerta tipo="erro">{erro}</Alerta>}
      <label className="block text-sm">
        <span className="text-slate-600">
          {porLink ? 'Link de assinatura' : 'Número do processo'}
        </span>
        <input
          className="mt-1 w-full"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          placeholder={porLink ? 'https://…' : 'Ex.: 25/123.456-7'}
        />
      </label>
      <Botao carregando={salvando} onClick={salvar}>
        Salvar
      </Botao>
    </Cartao>
  );
}
